import { useEffect, useMemo, useRef, useState } from 'react';
import type { Plan } from '../model/types';
import { emptyPlan } from '../model/defaults';
import { runProjection } from '../engine/projection';
import { CONSTANTS_AS_OF } from '../engine/data/federal';
import {
  decodePlanFromFragment,
  encodePlanToFragment,
  planFromFileJson,
  planToFileJson,
} from '../persist/encode';
import { Inputs } from './Inputs';
import { AdvancedInputs } from './AdvancedInputs';
import { Launcher } from './Launcher';
import { Wizard } from './Wizard';
import { netWorthToday } from '../model/netWorth';
import {
  clearLocalPlan, hasLocalPlan, loadLocalPlan, saveLocalPlan,
  clearWizardDraft, loadWizardDraft, type WizardDraft,
} from '../persist/local';
import { NetWorthChart } from './NetWorthChart';
import { GuaranteedChart } from './GuaranteedChart';
import { RelocationCompare } from './RelocationCompare';
import { SocialSecurityExplorer } from './SocialSecurityExplorer';
import { AffordCard } from './AffordCard';
import { MonteCarloCard } from './MonteCarloCard';
import { RothExplorerCard } from './RothExplorerCard';
import { YearTable } from './YearTable';
import { ScenariosCard } from './ScenariosCard';
import { CoachCard, WellnessCard } from './WellnessCoachCards';
import { InsightsCard } from './InsightsCard';
import { ReportView } from './ReportView';
import { claimingGrid } from '../engine/optimizer';
import { snapshotsToCsv, downloadText } from './csv';
import { fmtCompactUsd, fmtPct } from './format';
import type { Scenario } from '../scenarios/scenario';

type Theme = 'auto' | 'light' | 'dark';

export function App() {
  // Starts blank on purpose: no sample figures a user could mistake for theirs.
  const [plan, setPlan] = useState<Plan>(emptyPlan);
  // The front door: launcher unless a share link brought its own plan.
  const [view, setView] = useState<'launcher' | 'wizard-simple' | 'wizard-advanced' | 'app'>(
    () => (window.location.hash.length > 1 ? 'app' : 'launcher'),
  );
  const [remember, setRemember] = useState<boolean>(hasLocalPlan);
  const [wizardDraft, setWizardDraft] = useState<WizardDraft | null>(null);
  const [scenarios, setScenarios] = useState<Scenario[]>([]);
  const [todaysDollars, setTodaysDollars] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [shareState, setShareState] = useState<'idle' | 'copied' | 'warned'>('idle');
  const [theme, setTheme] = useState<Theme>('auto');
  const [inputMode, setInputMode] = useState<'simple' | 'advanced'>('simple');
  const [reportMode, setReportMode] = useState(false);
  const heroBarRef = useRef<HTMLDivElement>(null);
  const privacyRef = useRef<HTMLDialogElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // Load from the URL fragment once on start (user-initiated share links).
  useEffect(() => {
    const frag = window.location.hash;
    if (frag && frag.length > 1) {
      decodePlanFromFragment(frag)
        .then((bundle) => {
          setPlan(bundle.plan);
          setScenarios(bundle.scenarios);
          setView('app');
        })
        .catch((e: Error) => {
          setLoadError(e.message);
          setPlan(emptyPlan());
          setView('app');
        });
    }
  }, []);

  useEffect(() => {
    if (theme === 'auto') delete document.documentElement.dataset.theme;
    else document.documentElement.dataset.theme = theme;
  }, [theme]);

  // The slim headline bar slides in once the full summary scrolls away. It is
  // a FIXED overlay toggled straight on the DOM node: nothing about it touches
  // document flow (so content below never shifts) and it never re-renders
  // React during scrolling (so the charts stay put). Both matter for smoothness.
  useEffect(() => {
    const el = heroBarRef.current;
    if (!el) return;
    let queued = false;
    const onScroll = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        const y = window.scrollY;
        // Two thresholds: a single one flickers when you hover near the edge.
        const show = el.classList.contains('show') ? y > 150 : y > 260;
        el.classList.toggle('show', show);
        queued = false;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [reportMode, view]);

  // "Remember on this device": while enabled, keep the local copy current.
  // Only in the app view — launcher/wizard drafts must never clobber a saved
  // plan with a blank one.
  useEffect(() => {
    if (remember && view === 'app') {
      saveLocalPlan(plan, scenarios);
      // Working in the app makes any mid-wizard draft stale — resuming should
      // reopen this plan, not an old setup session.
      clearWizardDraft();
    }
  }, [remember, view, plan, scenarios]);

  const projection = useMemo(() => runProjection(plan, 2026), [plan]);
  // "Today" means the balances entered, not the end of this projection year.
  const netWorthNow = useMemo(() => netWorthToday(plan), [plan]);
  const snaps = projection.snapshots;
  const person = plan.household.people[0]!;

  const retirementYear = person.birthYear + person.retirementAge;
  const atRetirement = snaps.find((s) => s.year === retirementYear);
  const last = snaps[snaps.length - 1];

  const lifetimeTax = snaps.reduce((t, s) => t + s.totalTaxLiability, 0);
  const penaltyYears = snaps.filter((s) => s.earlyWithdrawalPenalty > 0).length;

  // Claiming-efficiency delta, shared by the wellness metrics and the coach.
  const claimingDeltaNetWorth = useMemo(() => {
    const g = claimingGrid(plan);
    return Math.max(0, g.best.endingNetWorth - g.current.endingNetWorth);
  }, [plan]);

  async function share() {
    const frag = await encodePlanToFragment(plan, scenarios);
    const url = `${window.location.origin}${window.location.pathname}#${frag}`;
    await navigator.clipboard.writeText(url);
    setShareState(shareState === 'idle' ? 'warned' : 'copied');
    setTimeout(() => setShareState('idle'), 6000);
  }

  function exportJson() {
    downloadText(
      'retirement-plan.json',
      planToFileJson(plan, CONSTANTS_AS_OF, scenarios),
      'application/json',
    );
  }

  function exportCsv() {
    downloadText('retirement-projection.csv', snapshotsToCsv(snaps, todaysDollars));
  }

  function importJson(file: File) {
    file.text().then((text) => {
      try {
        const bundle = planFromFileJson(text);
        setPlan(bundle.plan);
        setScenarios(bundle.scenarios);
        setLoadError(null);
      } catch (e) {
        setLoadError((e as Error).message);
      }
    });
  }

  const dollarsLabel = todaysDollars ? "today's dollars" : 'future (nominal) dollars';

  if (reportMode) {
    return (
      <ReportView
        plan={plan}
        snapshots={snaps}
        todaysDollars={todaysDollars}
        onClose={() => setReportMode(false)}
      />
    );
  }

  if (view === 'launcher') {
    // A mid-setup draft outranks the plan copy: resuming should reopen the
    // wizard on the step where the break happened, answers intact.
    const draft = loadWizardDraft();
    return (
      <Launcher
        savedAvailable={hasLocalPlan()}
        resumeKind={draft ? 'wizard' : 'plan'}
        resumeDetail={draft
          ? `${draft.tier === 'simple' ? 'Quick' : 'Full'} setup · you were on step ${draft.stepIndex + 1}`
          : undefined}
        onContinueSaved={() => {
          const d = loadWizardDraft();
          if (d) {
            setWizardDraft(d);
            setRemember(true);
            setView(d.tier === 'simple' ? 'wizard-simple' : 'wizard-advanced');
            return;
          }
          const bundle = loadLocalPlan();
          if (bundle) {
            setPlan(bundle.plan);
            setScenarios(bundle.scenarios);
            setRemember(true);
          }
          setView('app');
        }}
        onSimpleWizard={() => { setWizardDraft(null); setView('wizard-simple'); }}
        onAdvancedWizard={() => { setWizardDraft(null); setView('wizard-advanced'); }}
        onManual={() => setView('app')}
        onLoadFile={(file) => {
          importJson(file);
          setView('app');
        }}
      />
    );
  }

  if (view === 'wizard-simple' || view === 'wizard-advanced') {
    return (
      <Wizard
        tier={view === 'wizard-simple' ? 'simple' : 'advanced'}
        onFinish={(built) => {
          setPlan(built);
          setView('app');
        }}
        onExit={() => setView('launcher')}
        onRememberChange={setRemember}
        initialDraft={wizardDraft ?? undefined}
      />
    );
  }

  const endingLabel = projection.firstDepletionYear ? 'Runs out' : `At ${person.lifeExpectancyAge}`;
  const endingValue = projection.firstDepletionYear
    ? String(projection.firstDepletionYear)
    : fmtCompactUsd((last?.netWorth ?? 0) / (todaysDollars ? last?.cumulativeInflationFactor ?? 1 : 1));

  return (
    <div className="app">
      {/* Slim headline bar: fixed overlay, so it never affects page layout.
          aria-hidden because it mirrors the summary card verbatim. */}
      <div className="hero-bar" ref={heroBarRef} aria-hidden="true">
        <div className="hero-bar-inner">
          <div><span className="k">Now</span><span className="v">{fmtCompactUsd(netWorthNow)}</span></div>
          <div>
            <span className="k">At {person.retirementAge}</span>
            <span className="v">
              {atRetirement
                ? fmtCompactUsd(
                    atRetirement.netWorth /
                      (todaysDollars ? atRetirement.cumulativeInflationFactor : 1),
                  )
                : '—'}
            </span>
          </div>
          <div>
            <span className="k">{endingLabel}</span>
            <span className={`v${projection.firstDepletionYear ? ' negative' : ''}`}>{endingValue}</span>
          </div>
          <div><span className="k">Taxes</span><span className="v">{fmtCompactUsd(lifetimeTax)}</span></div>
        </div>
      </div>

      <header className="topbar">
        <div className="brand">
          Retirement Planner
          <span className="sub">private · runs entirely in your browser</span>
        </div>
        <div className="topbar-actions">
          {/* People lose work by never scrolling to the save card — the
              remember toggle lives up here so it's seen before any data goes in. */}
          <button
            className={remember ? 'btn remember-on' : 'btn primary'}
            title={remember
              ? 'Your inputs save to this browser automatically. Click to stop and delete the copy.'
              : 'Keep your inputs in this browser so they survive closing the tab. Stored on this device only.'}
            onClick={() => {
              const on = !remember;
              setRemember(on);
              if (on) saveLocalPlan(plan, scenarios);
              else clearLocalPlan();
            }}
          >
            {remember ? '✓ Remembered on this device' : 'Remember on this device'}
          </button>
          <div className="seg" role="group" aria-label="Assumption profile">
            {plan.assumptionProfiles.map((p) => (
              <button
                key={p.label}
                aria-pressed={plan.activeAssumptionLabel === p.label}
                onClick={() => setPlan({ ...plan, activeAssumptionLabel: p.label })}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="seg" role="group" aria-label="Dollar display">
            <button aria-pressed={todaysDollars} onClick={() => setTodaysDollars(true)}>
              Today's $
            </button>
            <button aria-pressed={!todaysDollars} onClick={() => setTodaysDollars(false)}>
              Future $
            </button>
          </div>
          <div className="seg" role="group" aria-label="Theme">
            {(['auto', 'light', 'dark'] as Theme[]).map((t) => (
              <button key={t} aria-pressed={theme === t} onClick={() => setTheme(t)}>
                {t}
              </button>
            ))}
          </div>
        </div>
      </header>

      {loadError && (
        <div className="warnbox" role="alert">
          <strong>Couldn't load that plan:</strong> {loadError} Starting fresh instead — your
          data was not changed anywhere else.
        </div>
      )}

      <section className="card" aria-label="Plan summary">
        <div className="hero-row">
          <div className="stat">
            <div className="label">Net worth today</div>
            <div className="value">{fmtCompactUsd(netWorthNow)}</div>
            <div className="context">
              across {plan.accounts.length} account{plan.accounts.length === 1 ? '' : 's'}
            </div>
          </div>
          <div className="stat">
            <div className="label">At retirement ({retirementYear})</div>
            <div className="value">
              {atRetirement
                ? fmtCompactUsd(
                    atRetirement.netWorth /
                      (todaysDollars ? atRetirement.cumulativeInflationFactor : 1),
                  )
                : '—'}
            </div>
            <div className="context">age {person.retirementAge} · {dollarsLabel}</div>
          </div>
          <div className="stat">
            <div className="label">
              {projection.firstDepletionYear ? 'Money runs out' : `At age ${person.lifeExpectancyAge}`}
            </div>
            <div className={`value${projection.firstDepletionYear ? ' negative' : ''}`}>
              {projection.firstDepletionYear
                ? `${projection.firstDepletionYear}`
                : fmtCompactUsd(
                    (last?.netWorth ?? 0) / (todaysDollars ? last?.cumulativeInflationFactor ?? 1 : 1),
                  )}
            </div>
            <div className="context">
              {projection.firstDepletionYear
                ? `age ${projection.firstDepletionYear - person.birthYear} — plan needs work`
                : `ending balance · ${dollarsLabel}`}
            </div>
          </div>
          <div className="stat">
            <div className="label">Lifetime taxes</div>
            <div className="value">{fmtCompactUsd(lifetimeTax)}</div>
            <div className="context">
              federal + payroll + state est. · nominal · effective{' '}
              {fmtPct(
                lifetimeTax /
                  Math.max(1, snaps.reduce((t, s) => t + Math.max(0, s.federal.agi), 0)),
              )}
            </div>
          </div>
        </div>

        <p className="disclaimer">
          This is an educational model built on your assumptions — not financial, tax, or legal
          advice. Tax law changes; constants here are dated {CONSTANTS_AS_OF}. For consequential
          decisions, talk to a professional with a fiduciary duty.
        </p>
      </section>

      {penaltyYears > 0 && (
        <div className="warnbox">
          This plan pays a 10% early-withdrawal penalty in {penaltyYears}{' '}
          {penaltyYears === 1 ? 'year' : 'years'} — and that is already after spending every
          penalty-free dollar first, so the bridge to 59½ is genuinely short. A Roth conversion
          ladder started five years earlier, or marking the employer plan you leave at 55+, can
          close it; see Worth a look for the specifics.
        </div>
      )}

      {(() => {
        const acaYears = snaps.filter((s) => s.acaFplRatio != null);
        const overCliff = acaYears.filter((s) => s.acaFplRatio! > 4.0);
        const nearCliff = acaYears.filter((s) => s.acaFplRatio! >= 3.5 && s.acaFplRatio! <= 4.0);
        if (overCliff.length > 0) {
          return (
            <div className="warnbox">
              <strong>ACA subsidy cliff:</strong> in {overCliff.length}{' '}
              {overCliff.length === 1 ? 'year' : 'years'} before Medicare, projected income is
              over 400% of the poverty level — past the cliff, so no premium credit at all.
              Lowering taxable income in those years (spending from savings or Roth instead of
              pre-tax accounts) could recover thousands per year.
            </div>
          );
        }
        if (nearCliff.length > 0) {
          return (
            <div className="warnbox">
              <strong>Close to the ACA cliff:</strong> in {nearCliff.length}{' '}
              {nearCliff.length === 1 ? 'year' : 'years'} before Medicare, projected income is
              within reach of 400% of the poverty level. One dollar over the line forfeits the
              entire premium credit — worth watching when planning withdrawals or Roth
              conversions.
            </div>
          );
        }
        return null;
      })()}

      <section className="card" aria-label="Your inputs">
        <h2 className="section-title">
          Your plan
          <span className="note">
            <span className="seg" role="group" aria-label="Input detail level">
              <button aria-pressed={inputMode === 'simple'} onClick={() => setInputMode('simple')}>
                Simple
              </button>
              <button aria-pressed={inputMode === 'advanced'} onClick={() => setInputMode('advanced')}>
                Advanced
              </button>
            </span>
          </span>
        </h2>
        {inputMode === 'simple'
          ? <Inputs plan={plan} onChange={setPlan} />
          : <AdvancedInputs plan={plan} onChange={setPlan} />}
      </section>

      <CoachCard plan={plan} projection={projection} claimingDeltaNetWorth={claimingDeltaNetWorth} />

      <WellnessCard plan={plan} projection={projection} claimingDeltaNetWorth={claimingDeltaNetWorth} />

      <ScenariosCard
        plan={plan}
        scenarios={scenarios}
        onPlanChange={setPlan}
        onScenariosChange={setScenarios}
        todaysDollars={todaysDollars}
      />

      <MonteCarloCard plan={plan} todaysDollars={todaysDollars} />

      <AffordCard plan={plan} onChange={setPlan} />

      <RothExplorerCard plan={plan} onChange={setPlan} />

      <SocialSecurityExplorer plan={plan} onChange={setPlan} />

      <RelocationCompare plan={plan} />

      <section className="card chart-card" aria-label="Guaranteed income vs must-spend">
        <h2 className="section-title">
          Guaranteed income vs. must-spend
          <span className="note">
            {(() => {
              const retSnaps = snaps.filter(
                (s) => s.wagesIncome + s.selfEmploymentIncome === 0 && s.essentialSpending > 0,
              );
              if (!retSnaps.length) return 'the gap is what your portfolio must cover';
              const mustSpend = (s: (typeof snaps)[number]) =>
                s.essentialSpending + s.healthcarePremiums + s.healthcareOOP + s.ltcNetCost;
              const covered =
                retSnaps.reduce((t, s) => t + Math.min(1, s.guaranteedIncome / mustSpend(s)), 0) /
                retSnaps.length;
              return `in retirement, guaranteed income covers ~${Math.round(covered * 100)}% of must-spend — the gap is what your portfolio must cover`;
            })()}
          </span>
        </h2>
        <GuaranteedChart snapshots={snaps} todaysDollars={todaysDollars} />
      </section>

      <InsightsCard snapshots={snaps} todaysDollars={todaysDollars} />

      <section className="card chart-card" aria-label="Balances over time">
        <h2 className="section-title">
          Account balances by tax type
          <span className="note">{dollarsLabel} · {projection.assumptionLabel} assumptions</span>
        </h2>
        <NetWorthChart snapshots={snaps} todaysDollars={todaysDollars} />
      </section>

      <section aria-label="Year by year detail">
        <h2 className="section-title" style={{ padding: '0 4px' }}>
          Year by year — the full numbers
          <span className="note">
            click a column to sort ·{' '}
            <button className="btn" onClick={exportCsv}>Download CSV</button>
          </span>
        </h2>
        <YearTable snapshots={snaps} todaysDollars={todaysDollars} />
      </section>

      <section className="card" aria-label="Save and share" style={{ marginTop: 'var(--space-6)' }}>
        <h2 className="section-title">Save or share this plan</h2>
        <p style={{ color: 'var(--ink-secondary)', marginTop: 0 }}>
          Nothing is stored anywhere automatically. Your plan lives in this tab until you save it.
        </p>
        <div className="topbar-actions">
          <button className="btn primary" onClick={share}>
            {shareState === 'idle' ? 'Copy shareable link' : 'Link copied ✓'}
          </button>
          <button className="btn" onClick={exportJson}>Download plan file</button>
          <button className="btn" onClick={() => fileRef.current?.click()}>Open plan file</button>
          <button className="btn" onClick={() => setReportMode(true)}>
            Print / PDF report
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="application/json"
            hidden
            onChange={(e) => e.target.files?.[0] && importJson(e.target.files[0])}
          />
        </div>
        {shareState === 'warned' && (
          <p className="disclaimer" role="status">
            The link itself contains your plan's financial inputs (nothing is uploaded — the data
            travels inside the link, after the “#”, which never reaches any server). Share it only
            with people you'd share the numbers with.
          </p>
        )}
        <div className="add-row" style={{ marginTop: 'var(--space-3)' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 'var(--fs-small)', color: 'var(--ink-secondary)' }}>
            <input
              type="checkbox"
              checked={remember}
              onChange={(e) => {
                const on = e.target.checked;
                setRemember(on);
                if (on) saveLocalPlan(plan, scenarios);
                else clearLocalPlan();
              }}
            />
            Remember on this device (stays in this browser only — never sent anywhere)
          </label>
          {remember && (
            <button
              className="btn danger"
              onClick={() => {
                clearLocalPlan();
                setRemember(false);
              }}
            >
              Forget this device
            </button>
          )}
        </div>
      </section>

      <footer className="footer">
        <span>
          Tax constants as of {CONSTANTS_AS_OF} · federal modeled in detail · state is a labeled
          approximation
        </span>
        <span>
          <a href="#privacy" onClick={(e) => { e.preventDefault(); privacyRef.current?.showModal(); }}>
            Privacy
          </a>
        </span>
      </footer>

      <dialog ref={privacyRef} className="privacy" aria-label="Privacy statement">
        <h2>Privacy, in plain language</h2>
        <p>
          This planner runs entirely in your browser. There is no server, no account, no
          analytics, no tracking, and no data collection of any kind. Nothing you type leaves
          your device unless <em>you</em> copy a link or download a file.
        </p>
        <p>
          A shareable link packs your plan into the part of the URL after the “#”, which browsers
          do not send to any server. A downloaded plan file is an ordinary JSON file on your
          device. Treat both with the same care as the numbers themselves.
        </p>
        <p>
          If you open the app fresh without a link, it starts completely blank — it has no
          memory of previous visits unless you turn on "Remember on this device." That option
          keeps a copy in this browser's local storage only — still on your device, still sent
          nowhere — and the "Forget this device" button deletes it instantly.
        </p>
        <button className="btn" onClick={() => privacyRef.current?.close()}>Close</button>
      </dialog>
    </div>
  );
}
